/**
 * Customer PWA analytics client — Analytics Core Engine v2.2.5 §2.3, §4.5, §9.2.
 *
 * Tenant-scoped HTTP wrapper around:
 *
 *   POST /api/{tenant}/v1/analytics/sessions
 *   POST /api/{tenant}/v1/analytics/sessions/:sessionId/complete
 *   POST /api/{tenant}/v1/analytics/sessions/:sessionId/abandon
 *   POST /api/{tenant}/v1/analytics/events
 *
 * Mirrors the kiosk client (`rpapp-kiosk/.../kioskAnalyticsClient.ts`) so
 * the wire contract is identical; the only differences are
 *   - `platform: 'MOBILE'` (vs `KIOSK`) by default
 *   - the abandon beacon path uses `/api/{tenant}/v1/...` directly so it
 *     works without the ApiClient being available on `pagehide`.
 *
 * The client is framework-free; React wiring lives in
 * `useCustomerAnalyticsManager`. Errors are surfaced to callers — the
 * manager always swallows them so analytics never break checkout.
 */
import { parseAnalyticsStartSessionData } from 'pi-kiosk-shared/analyticsApiTypes';
import {
  ANALYTICS_EVENT_CATALOG_VERSION,
  type AnalyticsEventName,
  type AnalyticsFlow,
  type AnalyticsTelemetryClass,
} from 'pi-kiosk-shared/analyticsEvents';
import type { CustomerAcquisitionSource } from '../../features/discovery/resolveCustomerAcquisitionSource.js';
import type { ApiClient } from '../api/ApiClient.js';
import { isHttpStormLatched } from '../http/httpStormDetectedLatch.js';

export interface CustomerAnalyticsConsentPayload {
  readonly version: string;
  readonly decidedAtMs: number;
}

export interface CustomerStartSessionInput {
  /**
   * Plan §1.4 — every analytics session needs a stable client-supplied
   * UUID. The PWA passes `customer_checkout_sessions.publicId` when one
   * exists so the analytics session and the commerce session share the
   * same identifier, otherwise it generates a fresh UUIDv4.
   */
  readonly sessionId?: string;
  readonly salesPointId?: number | null;
  /** @deprecated use salesPointId — kept for call-site compatibility */
  readonly kioskId?: number | null;
  readonly platform?: 'KIOSK' | 'MOBILE';
  readonly flow?: AnalyticsFlow | null;
  readonly source?: CustomerAcquisitionSource | null;
  readonly anonymousKey?: string | null;
  readonly metadata?: Record<string, unknown>;
}

export interface CustomerStartSessionResult {
  readonly sessionId: string;
  readonly created: boolean;
  readonly isClosed: boolean;
}

export interface CustomerEmitEventInput {
  readonly eventName: AnalyticsEventName;
  readonly catalogVersion?: 1;
  readonly clientEventId?: string;
  readonly paymentId?: string | null;
  readonly transactionId?: number | null;
  readonly flow?: AnalyticsFlow | null;
  readonly telemetryClass?: AnalyticsTelemetryClass;
  readonly anonymousKey?: string | null;
  readonly metadata?: Record<string, unknown>;
  readonly screenName?: string;
  readonly previousScreenName?: string;
  readonly failureReason?: string | null;
}

export interface CustomerAnalyticsClient {
  startSession(
    input: CustomerStartSessionInput,
  ): Promise<CustomerStartSessionResult>;
  emit(sessionId: string, input: CustomerEmitEventInput): Promise<void>;
  completeSession(sessionId: string): Promise<void>;
  abandonSession(sessionId: string, reason?: string): Promise<void>;
  patchSessionConsentRevoked(sessionId: string): Promise<void>;
  /** Clears cached session-auth token after a closed-session recovery. */
  resetSessionAuth(): void;
  /** Best-effort revoke when no session id is available (P3-03). */
  revokeConsentByKey(anonymousKey: string): Promise<void>;
  /**
   * Best-effort `session_abandoned` from `pagehide` / `visibilitychange`.
   * Returns true if the beacon was queued by the browser. The PWA tolerates
   * dropped beacons (it just means the abandon is recorded later when the
   * server-side session_timeout sweeper runs).
   */
  abandonSessionBeacon(
    tenantCode: string,
    apiBaseUrl: string,
    sessionId: string,
    reason: string,
  ): boolean;
}

function uuidv4(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

const ANALYTICS_SESSION_AUTH_HEADER = 'X-Analytics-Session-Auth';

function readIsClosedFromStartSessionData(data: unknown): boolean {
  if (typeof data !== 'object' || data === null) {
    return false;
  }
  const session = (data as Record<string, unknown>).session;
  if (typeof session !== 'object' || session === null) {
    return false;
  }
  const record = session as Record<string, unknown>;
  return record.completed === true || record.abandoned === true;
}

export function createCustomerAnalyticsClient(
  api: ApiClient,
): CustomerAnalyticsClient {
  let sessionAuthToken: string | null = null;

  return {
    async startSession(input) {
      const data = await api.request<unknown>('/analytics/sessions', {
        method: 'POST',
        body: {
          sessionId: input.sessionId ?? uuidv4(),
          salesPointId: input.salesPointId ?? input.kioskId ?? null,
          platform: input.platform ?? 'MOBILE',
          flow: input.flow ?? null,
          source: input.source ?? null,
          anonymousKey: input.anonymousKey ?? null,
          metadata: input.metadata ?? {},
        },
      });
      const parsed = parseAnalyticsStartSessionData(data);
      sessionAuthToken = parsed.sessionAuthToken ?? null;
      const isClosed =
        'isClosed' in parsed && parsed.isClosed === true
          ? true
          : readIsClosedFromStartSessionData(data);
      return {
        sessionId: parsed.sessionId,
        created: parsed.created,
        isClosed,
      };
    },

    resetSessionAuth() {
      sessionAuthToken = null;
    },

    /** Tier-2 events send `anonymousKey` when granted; proof is enforced via server session latch (FIX-19), not event metadata. */
    async emit(sessionId, input) {
      const headers =
        sessionAuthToken !== null
          ? { [ANALYTICS_SESSION_AUTH_HEADER]: sessionAuthToken }
          : undefined;
      await api.request<unknown>('/analytics/events', {
        method: 'POST',
        headers,
        body: {
          sessionId,
          eventName: input.eventName,
          catalogVersion: input.catalogVersion ?? ANALYTICS_EVENT_CATALOG_VERSION,
          clientEventId: input.clientEventId ?? uuidv4(),
          paymentId: input.paymentId ?? null,
          transactionId: input.transactionId ?? null,
          flow: input.flow ?? null,
          telemetryClass: input.telemetryClass ?? 'OPERATIONAL',
          anonymousKey: input.anonymousKey ?? null,
          metadata: input.metadata ?? {},
          screenName: input.screenName,
          previousScreenName: input.previousScreenName,
          failureReason: input.failureReason ?? null,
        },
      });
    },

    async completeSession(sessionId) {
      await api.request<unknown>(
        `/analytics/sessions/${encodeURIComponent(sessionId)}/complete`,
        { method: 'POST', body: {} },
      );
    },

    async abandonSession(sessionId, reason) {
      await api.request<unknown>(
        `/analytics/sessions/${encodeURIComponent(sessionId)}/abandon`,
        { method: 'POST', body: { abandonReason: reason ?? 'route_leave' } },
      );
    },

    async patchSessionConsentRevoked(sessionId) {
      await api.request<unknown>(
        `/analytics/sessions/${encodeURIComponent(sessionId)}/consent`,
        { method: 'PATCH', body: { revoked: true } },
      );
    },

    async revokeConsentByKey(anonymousKey) {
      await api.request<unknown>('/analytics/consent/revoke-by-key', {
        method: 'POST',
        body: { anonymousKey },
      });
    },

    abandonSessionBeacon(tenantCode, apiBaseUrl, sessionId, reason) {
      // G9 — sendBeacon bypasses installCustomerHttpStormFetchGuard; skip while latched.
      if (isHttpStormLatched()) {
        return false;
      }
      if (typeof navigator === 'undefined' || !('sendBeacon' in navigator)) {
        return false;
      }
      const trimmedBase = apiBaseUrl.replace(/\/+$/, '');
      const url =
        `${trimmedBase}/api/${encodeURIComponent(tenantCode)}/v1/analytics/sessions/` +
        `${encodeURIComponent(sessionId)}/abandon`;
      const body = new Blob(
        [JSON.stringify({ abandonReason: reason })],
        { type: 'application/json' },
      );
      return navigator.sendBeacon(url, body);
    },
  };
}

const sessionAuthByTenant = new Map<string, { sessionId: string; sessionAuthToken: string | null }>();

/**
 * Platform account chrome has no API tenant, so the analytics manager queues
 * and never posts. Receipt events belong to the payment's org — start a
 * session and emit on that tenant.
 */
export async function emitCustomerAnalyticsOnTenant(
  api: ApiClient,
  tenantCode: string,
  input: CustomerEmitEventInput,
): Promise<void> {
  const scopedTenant = tenantCode.trim();
  if (scopedTenant.length === 0) {
    return;
  }
  let session = sessionAuthByTenant.get(scopedTenant);
  if (session === undefined) {
    const data = await api.request<unknown>('/analytics/sessions', {
      method: 'POST',
      tenantCode: scopedTenant,
      body: {
        sessionId: uuidv4(),
        platform: 'MOBILE',
        metadata: {},
      },
    });
    const parsed = parseAnalyticsStartSessionData(data);
    session = {
      sessionId: parsed.sessionId,
      sessionAuthToken: parsed.sessionAuthToken ?? null,
    };
    sessionAuthByTenant.set(scopedTenant, session);
  }
  const headers =
    session.sessionAuthToken !== null
      ? { [ANALYTICS_SESSION_AUTH_HEADER]: session.sessionAuthToken }
      : undefined;
  await api.request<unknown>('/analytics/events', {
    method: 'POST',
    tenantCode: scopedTenant,
    ...(headers !== undefined ? { headers } : {}),
    body: {
      sessionId: session.sessionId,
      eventName: input.eventName,
      catalogVersion: input.catalogVersion ?? ANALYTICS_EVENT_CATALOG_VERSION,
      clientEventId: input.clientEventId ?? uuidv4(),
      paymentId: input.paymentId ?? null,
      transactionId: input.transactionId ?? null,
      flow: input.flow ?? null,
      telemetryClass: input.telemetryClass ?? 'OPERATIONAL',
      anonymousKey: input.anonymousKey ?? null,
      metadata: input.metadata ?? {},
      screenName: input.screenName,
      previousScreenName: input.previousScreenName,
      failureReason: input.failureReason ?? null,
    },
  });
}
